import { Star, ExternalLink, Image, Film, ImageOff, Loader2, Trash2 } from 'lucide-react'
import type { Idea } from '@/types'
import { cn, isVideoUrl } from '@/lib/utils'

// Source display config
const SOURCE_STYLES: Record<string, { label: string; className: string }> = {
  foreplay: { label: 'Ads Research', className: 'bg-amber-100 text-amber-700' },
  foreplay_ad_search: { label: 'Ad Search', className: 'bg-amber-100 text-amber-700' },
  foreplay_brand_ads: { label: 'Brand Ads', className: 'bg-amber-100 text-amber-700' },
  perplexity: { label: 'Web Research', className: 'bg-green-100 text-green-700' },
  perplexity_search: { label: 'Web Search', className: 'bg-green-100 text-green-700' },
  perplexity_sonar: { label: 'AI Chat', className: 'bg-green-100 text-green-700' },
  deep_research: { label: 'Deep Research', className: 'bg-emerald-100 text-emerald-700' },
  document: { label: 'Document Analysis', className: 'bg-blue-100 text-blue-700' },
  vertical_ideas_novel: { label: 'Novel Ideas', className: 'bg-rose-100 text-rose-700' },
  vertical_ideas_ecommerce: { label: 'Ecom Ideas', className: 'bg-sky-100 text-sky-700' },
  vertical_ideas_finance: { label: 'Finance Ideas', className: 'bg-violet-100 text-violet-700' },
  manual: { label: 'Manual', className: 'bg-gray-100 text-gray-700' },
}

interface IdeaCardProps {
  idea: Idea
  onFavorite: (idea: Idea) => void
  onDelete: (idea: Idea) => void
  isDeleting?: boolean
  onClick: (idea: Idea) => void
  selectionMode?: boolean
  selected?: boolean
  onSelect?: (idea: Idea) => void
}

export function IdeaCard({ idea, onFavorite, onDelete, isDeleting = false, onClick, selectionMode = false, selected = false, onSelect }: IdeaCardProps) {
  const sourceStyle = SOURCE_STYLES[idea.source] || SOURCE_STYLES.manual
  const hasMedia = idea.media_urls && idea.media_urls.length > 0
  const extraMediaCount = hasMedia ? idea.media_urls.length - 1 : 0
  const videoCount = hasMedia ? idea.media_urls.filter((url) => isVideoUrl(url)).length : 0
  const imageCount = hasMedia ? idea.media_urls.length - videoCount : 0
  const sourceMetadata = idea.source_metadata && typeof idea.source_metadata === 'object' ? idea.source_metadata : null
  const referenceMode =
    sourceMetadata && typeof sourceMetadata['reference_mode'] === 'string'
      ? sourceMetadata['reference_mode']
      : null
  const hasNovelReferenceMedia = hasMedia && referenceMode === 'novel_reference'
  const styleReference =
    sourceMetadata &&
    typeof sourceMetadata['style_reference'] === 'object' &&
    sourceMetadata['style_reference'] !== null
      ? (sourceMetadata['style_reference'] as Record<string, unknown>)
      : null
  const styleReferenceFileName =
    styleReference && typeof styleReference['file_name'] === 'string' ? styleReference['file_name'] : null

  return (
    <div
      className={cn(
        'bg-white rounded-lg border hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden group break-inside-avoid mb-3 relative',
        selected ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200'
      )}
      onClick={() => selectionMode && onSelect ? onSelect(idea) : onClick(idea)}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div
          className="absolute top-2 right-2 z-20"
          onClick={(e) => {
            e.stopPropagation()
            onSelect?.(idea)
          }}
        >
          <div
            className={cn(
              'w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
              selected
                ? 'bg-primary border-primary text-white'
                : 'bg-white/90 border-gray-300 hover:border-primary'
            )}
          >
            {selected && (
              <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Media Thumbnail - only when media exists */}
      {hasMedia && (
        <div className="relative aspect-video bg-gray-100 overflow-hidden">
          {isVideoUrl(idea.media_urls[0]) ? (
            <video
              src={idea.media_urls[0]}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              preload="metadata"
            />
          ) : (
            <img
              src={idea.media_urls[0]}
              alt={idea.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )}
          {extraMediaCount > 0 && (
            <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 bg-black/60 text-white text-xs rounded-full">
              +{extraMediaCount}
            </div>
          )}
          {hasNovelReferenceMedia && (
            <div className="absolute top-1.5 right-1.5 z-10 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-white/90 text-gray-700 shadow-sm">
              <Image className="h-3 w-3" />
              Ref Image
            </div>
          )}
          {/* Media count badge on thumbnail */}
          <div className="absolute top-1.5 left-1.5 z-10 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/90 text-white">
            {imageCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Image className="h-3 w-3" />
                {imageCount}
              </span>
            )}
            {imageCount > 0 && videoCount > 0 && (
              <span className="text-white/60">|</span>
            )}
            {videoCount > 0 && (
              <span className="inline-flex items-center gap-0.5">
                <Film className="h-3 w-3" />
                {videoCount}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="p-3 space-y-2">
        {/* Title + Favorite */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-sm text-gray-900 line-clamp-1 flex-1">{idea.title}</h3>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onFavorite(idea)
            }}
            className="p-0.5 rounded-full hover:bg-gray-100 transition-colors shrink-0"
          >
            <Star
              className={cn(
                'h-3.5 w-3.5',
                idea.is_favorited
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300 hover:text-yellow-400'
              )}
            />
          </button>
        </div>

        {/* Description */}
        <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{idea.description}</p>

        {/* Badges row: Source + Media indicator (for no-media) + Tags */}
        <div className="flex flex-wrap items-center gap-1">
          <span
            className={cn(
              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium',
              sourceStyle.className
            )}
          >
            {sourceStyle.label}
            {idea.source_url && <ExternalLink className="h-2.5 w-2.5" />}
          </span>

          {/* Inline media indicator for cards without thumbnail */}
          {!hasMedia && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400">
              <ImageOff className="h-3 w-3" />
              No media
            </span>
          )}

          {hasNovelReferenceMedia && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
              Ref
              {styleReferenceFileName ? `: ${styleReferenceFileName}` : ' image'}
            </span>
          )}

          {idea.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="px-1.5 py-0.5 rounded-full text-xs bg-gray-50 text-gray-500 border border-gray-200"
            >
              {tag}
            </span>
          ))}
          {idea.tags.length > 2 && (
            <span className="text-xs text-gray-400">+{idea.tags.length - 2}</span>
          )}
        </div>

        {/* Footer: Used count */}
        <div className="flex items-center justify-between">
          <span
            className={cn(
              'text-xs px-1.5 py-0.5 rounded-full',
              idea.used_count > 0
                ? 'bg-green-50 text-green-700 font-medium'
                : 'text-gray-400'
            )}
          >
            {idea.used_count > 0 ? `Used ${idea.used_count}x` : 'Unused'}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete(idea)
            }}
            disabled={isDeleting}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}
